import { action, flow, makeObservable, observable } from 'mobx';
import { GlobalStore } from '../../../stores';
import { ExternalLenderStore } from '../../companyProfile/stores/externalLenderStore';
import { LenderDetailsFormStore } from './lenderDetailsFormStore';
import { LenderDocumentsStore } from './lenderDocumentsStore';
import { LenderService } from 'libs/feature-app-core/src/services/lenderService';

export class LenderDetailsStore {
  protected globalStore: GlobalStore;
  private lenderService: LenderService;

  salesforceLenderStore: ExternalLenderStore;
  lenderDetailsFormStore: LenderDetailsFormStore;
  lenderDocumentsStore: LenderDocumentsStore;
  selectedBrokerProspect: number;
  isAddBrokerModalOpen: boolean;
  isAddBrokerAsTPOModalOpen: boolean;
  selectedBrokerOwner: any;
  allForeclosureAttorneys: any[] = [];
  savedForeclosureAttorney: any = null;

  RESTRICTED_STATUSES = ['Approved', 'Committee Decision'];
  constructor(globalStore, salesforceLenderStore) {
    this.globalStore = globalStore;
    this.salesforceLenderStore = salesforceLenderStore;
    this.lenderService = new LenderService();

    this.lenderDetailsFormStore = new LenderDetailsFormStore(
      globalStore,
      salesforceLenderStore
    );
    this.lenderDocumentsStore = new LenderDocumentsStore(
      globalStore,
      this.lenderDetailsFormStore
    );

    this.globalStore = globalStore;

    makeObservable(this, {
      initializeForm: flow,
      submitForm: flow,
      validateAndUpdateLender: flow,
      updateEntityInformation: flow,
      updateRMLenderInformation: flow,
      validateRMorPercentageChangedForCommissions: action,
      updateCommissions: flow,
      validationsForSavingLender: flow,
      saveTamariskInformation: flow,
      isAddBrokerModalOpen: observable,
      setAddBrokerRelationModalOpen: action,
      addLenderBrokerRelation: flow,
      selectedBrokerOwner: observable,
      isAddBrokerAsTPOModalOpen: observable,
      setAddBrokerAsTPOModalOpen: action,
      addBrokerAsTPO: flow,
      saveHighValueClientValue: flow,
      allForeclosureAttorneys: observable,
      fetchAllForeclosureAttorneys: flow,
      savedForeclosureAttorney: observable,
      getForeclosureAttorneyByLoanId: flow,
    });
  }

  *validateAndUpdateLender(isSubmitting = false) {
    try {
      this.lenderDetailsFormStore.runFormValidationWithMessage();
      if (!this.lenderDetailsFormStore.form.meta.isValid) {
        return;
      }
      yield this.lenderDetailsFormStore.saveSalesforceLenderChanges(
        isSubmitting
      );

      if (isSubmitting) {
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Lender details submitted.',
        });
      }
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while updating lender details.',
      });
    }
  }

  validateRMorPercentageChangedForCommissions() {
    const changed = this.lenderDetailsFormStore.validateRMorPercentageChanged();
    return changed;
  }

  *updateCommissions() {
    try {
      yield this.lenderDetailsFormStore.updateCommissions();
    } catch (e) {
      console.log('error', e);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while updating Loans Commissions details.',
      });
    }
  }

  *updateEntityInformation() {
    try {
      const validation = yield this.validationsForSavingLender();
      if (validation) {
        yield this.lenderDetailsFormStore.saveSalesforceLenderChanges(false);
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Entity details saved.',
        });
      }
    } catch (e) {
      console.log('error', e);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while updating entity details.',
      });
    }
  }

  *updateRMLenderInformation(removeFromCurrentJourneys?) {
    try {

      yield this.lenderDetailsFormStore.saveSalesforceLenderChanges(false, removeFromCurrentJourneys);
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Entity details saved.',
      });
      return true;
    } catch (e) {
      const error = e?.error?.response?.data?.error;
      const message =
        error?.errorData?.errorCode === 'IN_JOURNEY'
          ? 'This TPO has a contact currently active in a cadence. Check "Remove from current journeys" to reassign it anyway.'
          : error?.message;
      this.globalStore.notificationStore.showErrorNotification({
        message:
          message ||
          'An error occurred when trying to assign a new owner for this record',
      });
      return false;
    }
  }

  *initializeForm() {
    yield Promise.all([
      this.lenderDetailsFormStore.loadData(),
      this.lenderDetailsFormStore.getLenderBankInformation(),
    ]);
    yield this.lenderDocumentsStore.loadData();
  }

  *submitForm() {
    yield this.validateAndUpdateLender(true);
  }

  *validationsForSavingLender() {
    try {

      if (this.portalValidation()) {

        const isDuplicateAlias = yield this.lenderDetailsFormStore.isDuplicateLenderAlias();
        if (isDuplicateAlias?.data?.data) {
          this.globalStore.notificationStore.showErrorNotification({
            message: 'This Alias is not valid, please choose a different one',
          });
          return false;
        }

        if (!this.salesforceLenderStore.salesForceLender?.isInternal && !(this.salesforceLenderStore.salesForceLender?.lenderName.length <= 10)) {
          this.globalStore.notificationStore.showErrorNotification({
            message: 'Alias must be less or equal to 10 characters',
          });
          return false;
        }
      }

      return true;
    } catch (e) {
      console.log('error', e);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while validating lender.',
      });
      return false;
    }
  }

  portalValidation() {
    if (this.globalStore.isInternalPortal) {
      return this.RESTRICTED_STATUSES.includes(this.salesforceLenderStore.salesForceLender?.status);
    } else {
      return this.RESTRICTED_STATUSES.includes(this.salesforceLenderStore.salesForceLender?.status) && this.globalStore?.userFeatures?.backOfficeUser;
    }
  }

  saveTamariskInformation() {
    const lender = this.lenderDetailsFormStore.salesforceLenderStore.salesForceLender;

    const preference = {
      lenderId: lender.lenderId,
      tamariskSendInvoiceEmail: this.parseBoolean(this.lenderDetailsFormStore.form.fields.tamariskSendInvoiceEmail.value),
      tamariskDefaultOption: this.parseBoolean(this.lenderDetailsFormStore.form.fields.tamariskDefaultOptionModal.value),
      tamariskAppraisal: this.parseBoolean(this.lenderDetailsFormStore.form.fields.tamariskAppraisalModal.value),
    }
    this.salesforceLenderStore.updateTamariskPreferences(preference)
  }

  parseBoolean(value) {
    return value === true || value === 'Yes' || value === 'yes' ? true : false;
  }

  saveDesktopAppraisalPreferences() {
    const lender = this.lenderDetailsFormStore.salesforceLenderStore.salesForceLender;
    this.salesforceLenderStore.updateDesktopAppraisalPreferences(this.lenderDetailsFormStore.form.fields.desktopAppraisalPreference.value, lender.lenderId);
  }

  setAddBrokerRelationModalOpen(record: any, isOpen: boolean) {
    this.selectedBrokerProspect = record;
    this.isAddBrokerModalOpen = isOpen;

  }

  setAddBrokerAsTPOModalOpen(record: any, isOpen: boolean) {
    this.selectedBrokerProspect = record;
    this.isAddBrokerAsTPOModalOpen = isOpen;

  }

  *addLenderBrokerRelation(callback: () => void) {
    if (this.selectedBrokerOwner) {
      yield this.salesforceLenderStore.addLenderBrokerRelation(this.selectedBrokerOwner, this.selectedBrokerProspect);
      this.selectedBrokerOwner = null;
      callback();
    } else {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Select an owner for the broker relation'
      });
    }
  }

  *addBrokerAsTPO(callback: () => void) {
    if (this.selectedBrokerOwner) {
      yield this.salesforceLenderStore.turnBrokerIntoTPO(this.selectedBrokerOwner, this.selectedBrokerProspect);
      this.selectedBrokerOwner = null;
      callback();
    } else {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Select a relationship manager for the TPO'
      });
    }
  }

  *saveHighValueClientValue() {
    try {
      yield this.lenderDetailsFormStore.saveHighValueClientTPO();
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'High Value Client details saved.',
      });
    } catch (e) {
      console.log('error', e);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while updating High Value Client details.',
      });
    }
  }

  getAllForeclosureAttorneys() {
    void this.fetchAllForeclosureAttorneys();
  }

  *fetchAllForeclosureAttorneys() {
    if (this.allForeclosureAttorneys.length === 0) {
      try {
        const response = yield this.lenderService.getAllForeclosureAttorneys();
        this.allForeclosureAttorneys = JSON.parse(response.data.data);
      } catch (e) {
        this.globalStore.notificationStore.showErrorNotification({
          message: 'Error while fetching the foreclosure attorneys',
        });
      }
    }
  }

  *getForeclosureAttorneyByLoanId(loanId: string) {
    try {
      const response = yield this.lenderService.getForeclosureAttorneyByLoanId(loanId);
      this.savedForeclosureAttorney = JSON.parse(response.data.data);
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while fetching foreclosure attorney',
      });
    }
  }

}
